import React from "react";
import { Typography, Tag, Space } from "antd";
import * as con from "../Constants";

const { Text, Title } = Typography;

// Tour step content component
const TourStep = ({ title, subtitle, children, hint }) => (
  <div className="tour-content">
    {title && (
      <Title level={5} className="tour-title">
        {title}
      </Title>
    )}
    {subtitle && (
      <Text type="secondary" className="tour-subtitle">
        {subtitle}
      </Text>
    )}
    <div className="tour-body">
      {children}
    </div>
    {hint && (
      <div className={`tour-hint tour-hint--${hint.type || 'info'}`}>
        {hint.icon} {hint.text}
      </div>
    )}
  </div>
);

// Scroll function
const scrollToTarget = (element) => {
  element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
};

// Method descriptions component
const MethodDescriptions = ({ i18n }) => (
  <div className="tour-methods">
    <div className="tour-method">
      <Tag color="green">BLASTn</Tag>
      <Text>{i18n.t('tourBlastnDesc')}</Text>
    </div>
    <div className="tour-method">
      <Tag color="blue">MegaBLAST</Tag>
      <Text>{i18n.t('tourMegablastDesc')}</Text>
    </div>
    <div className="tour-method">
      <Tag color="orange">VSEARCH</Tag>
      <Text>{i18n.t('tourVsearchDesc')}</Text>
    </div>
    <div className="tour-method">
      <Tag color="red">EXACT</Tag>
      <Text>{i18n.t('tourExactDesc')}</Text>
    </div>
  </div>
);

// Tour configurations
const tourConfigs = {
  [con.PAGE_ALIGNMENT_RESULTS]: (i18n) => [
    {
      selector: '[data-tour="back-button"]',
      content: (
        <TourStep 
          title={i18n.t('tourBackButtonTitle')}
          subtitle="Step 1 of 4"
        >
          {i18n.t('tourBackButtonContent')}
        </TourStep>
      ),
      position: [100, 100],
      action: scrollToTarget
    },
    {
      selector: '[data-tour="query-dropdown"]',
      content: (
        <TourStep 
          title={i18n.t('tourQueryDropdownTitle')}
          subtitle="Step 2 of 4"
          hint={{ type: 'warning', icon: 'ℹ️', text: i18n.t('tourQueryDropdownNote') }}
        >
          {i18n.t('tourQueryDropdownContent')}
        </TourStep>
      ),
      position: 'center',
      action: scrollToTarget
    },
    {
      selector: '[data-tour="results-chart"]',
      content: (
        <TourStep 
          title={i18n.t('tourResultsChartTitle')}
          subtitle="Step 3 of 4"
          hint={{ type: 'info', icon: '💡', text: i18n.t('tourResultsChartHint') }}
        >
          {i18n.t('tourResultsChartContent')}
        </TourStep>
      ),
      position: 'center',
      action: scrollToTarget
    },
    {
      selector: '[data-tour="alignment-detail"]',
      content: (
        <TourStep 
          title={i18n.t('tourAlignmentDetailTitle')}
          subtitle="Step 4 of 4"
          hint={{ type: 'warning', icon: 'ℹ️', text: i18n.t('tourAlignmentDetailNote') }}
        >
          {i18n.t('tourAlignmentDetailContent')}
        </TourStep>
      ),
      position: 'center',
      action: scrollToTarget
    }
  ],

  [con.PAGE_ANNOTATION_RESULTS]: (i18n) => [
    {
      selector: '[data-tour="back-button"]',
      content: (
        <TourStep 
          title={i18n.t('tourBackButtonTitle')}
          subtitle="Step 1 of 4"
        >
          {i18n.t('tourBackButtonContent')}
        </TourStep>
      ),
      position: [100, 100],
      action: scrollToTarget
    },
    {
      selector: '[data-tour="results-table"]',
      content: (
        <TourStep 
          title={i18n.t('tourResultsTableTitle')}
          subtitle="Step 2 of 4"
        >
          {i18n.t('tourResultsTableContent')}
        </TourStep>
      ),
      position: 'center',
      action: scrollToTarget
    },
    {
      selector: '[data-tour="download-results"]',
      content: (
        <TourStep 
          title={i18n.t('tourDownloadTitle')}
          subtitle="Step 3 of 4"
          hint={{ type: 'info', icon: '📁', text: i18n.t('tourDownloadHint') }}
        >
          {i18n.t('tourDownloadContent')}
        </TourStep>
      ),
      position: 'bottom',
      action: scrollToTarget
    },
    {
      selector: '[data-tour="details-column"]',
      content: (
        <TourStep 
          title={i18n.t('tourDetailsColumnTitle')}
          subtitle="Step 4 of 4"
        >
          {i18n.t('tourDetailsColumnContent')}
        </TourStep>
      ),
      position: 'left',
      action: scrollToTarget
    }
  ],

  // Main page (default)
  default: (i18n) => [
    {
      selector: '[data-tour="database-selector"]',
      content: (
        <TourStep 
          title={i18n.t('tourDatabaseTitle')}
          subtitle="Step 1 of 7"
        >
          {i18n.t('tourDatabaseContent')}
        </TourStep>
      ),
      position: 'center',
      action: scrollToTarget
    },
    {
      selector: '[data-tour="sequence-input"]',
      content: (
        <TourStep 
          title={i18n.t('tourSequenceInputTitle')}
          subtitle="Step 2 of 7"
        >
          {i18n.t('tourSequenceInputContent')}
        </TourStep>
      ),
      position: 'right',
      action: scrollToTarget
    },
    {
      selector: '[data-tour="file-upload"]',
      content: (
        <TourStep 
          title={i18n.t('tourFileUploadTitle')}
          subtitle="Step 3 of 7"
        >
          {i18n.t('tourFileUploadContent')}
        </TourStep>
      ),
      position: 'left',
      action: scrollToTarget
    },
    {
      selector: '[data-tour="test-data"]',
      content: (
        <TourStep 
          title={i18n.t('tourTestDataTitle')}
          subtitle="Step 4 of 7"
        >
          {i18n.t('tourTestDataContent')}
        </TourStep>
      ),
      position: 'left',
      action: scrollToTarget
    },
    {
      selector: '[data-tour="validation-status"]',
      content: (
        <TourStep 
          title={i18n.t('tourValidationTitle')}
          subtitle="Step 5 of 7"
        >
          {i18n.t('tourValidationContent')}
        </TourStep>
      ),
      position: 'top',
      action: scrollToTarget
    },
    {
      selector: '[data-tour="analysis-methods"]',
      content: (
        <TourStep 
          title={i18n.t('tourMethodsTitle')}
          subtitle="Step 6 of 7"
        >
          <Space direction="vertical" size="small" style={{ width: '100%' }}>
            <Text>{i18n.t('tourMethodsContent')}</Text>
            <MethodDescriptions i18n={i18n} />
          </Space>
        </TourStep>
      ),
      position: 'top',
      action: scrollToTarget
    },
    {
      selector: '[data-tour="historic-results"]',
      content: (
        <TourStep 
          title={i18n.t('tourHistoricResultsTitle')}
          subtitle="Step 7 of 7"
          hint={{ type: 'info', icon: '💾', text: i18n.t('tourHistoricResultsHint') }}
        >
          {i18n.t('tourHistoricResultsContent')}
        </TourStep>
      ),
      position: 'bottom',
      padding: { popover: [-20, 0, 0, 0] },
      action: scrollToTarget
    }
  ]
};

// Tour steps getter
export const getTourSteps = (currentPage, i18n) => {
  const config = tourConfigs[currentPage] || tourConfigs.default;
  return config(i18n);
};
